import { useEffect, useState } from 'react'
import {
  OAUTH_PROVIDER_NAME, fetchEnabledOAuthProviders, startOAuthDirect, type OAuthProvider,
} from '@/lib/authDirect'

// "Sign in with Google / Microsoft" under the Login form. Renders nothing until
// GoTrue's public settings say a provider is switched on — and nothing at all
// if that read fails: the password form above still works, while a button for
// an unconfigured provider would strand the user on GoTrue's raw JSON error.
// Existing accounts only — see the before_user_created hook, mig 20261034000000.

const SETTINGS_TIMEOUT_MS = 8_000

export default function OAuthSignInButtons({ onError }: { onError: (message: string) => void }) {
  const [providers, setProviders] = useState<OAuthProvider[]>([])
  const [pending,   setPending]   = useState<OAuthProvider | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    // A timer, not AbortSignal.timeout(): that is Chrome 103+, and the floor
    // handsets run Chrome 86.
    const timer = setTimeout(() => ac.abort(), SETTINGS_TIMEOUT_MS)
    fetchEnabledOAuthProviders(ac.signal).then(setProviders, () => { /* no buttons — see header */ })
    return () => { clearTimeout(timer); ac.abort() }
  }, [])

  // Back from the provider's page restores this one from the bfcache with the
  // button still reading "Opening Google…" and disabled. Un-stick it.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) setPending(null) }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  if (providers.length === 0) return null

  async function pick(provider: OAuthProvider) {
    setPending(provider)
    const { error } = await startOAuthDirect(provider)
    // On success the page is already leaving; only a failure comes back here.
    if (error) {
      setPending(null)
      onError(`Couldn't open ${OAUTH_PROVIDER_NAME[provider]} sign-in on this browser. ` +
              'Sign in with your email and password instead.')
    }
  }

  return (
    <div className="mt-6 space-y-3">
      <div className="flex items-center gap-3 text-xs text-gray-500" aria-hidden="true">
        <span className="h-px flex-1 bg-gray-700" />or<span className="h-px flex-1 bg-gray-700" />
      </div>
      {providers.map(p => (
        <button
          key={p}
          type="button"
          disabled={pending !== null}
          onClick={() => void pick(p)}
          className="w-full min-h-[44px] inline-flex items-center justify-center gap-3 rounded-xl border border-gray-400
                     bg-white px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-60">
          {p === 'google' ? <GoogleMark /> : <MicrosoftMark />}
          {pending === p ? `Opening ${OAUTH_PROVIDER_NAME[p]}…` : `Sign in with ${OAUTH_PROVIDER_NAME[p]}`}
        </button>
      ))}
    </div>
  )
}

// The marks each provider's branding rules ask for, unaltered.
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}
